/**
 * Plans v2 card 3.4, after sign-in: what happens to an account's CLOUD boards
 * and documents when its plan has no `cloud_sync` (owner decisions 16.10.2026).
 *
 *   - A downgrade (`removal_reason: 'downgrade'`, or a flag from an older
 *     backend that sends no reason): NO automatic copy. The cloud items are
 *     read-only; the user is told ONCE, in the notification centre, the date
 *     the cloud copy will be deleted (`removal_scheduled_at`, 90-day
 *     retention) and how to keep it — "Download to this device" per item, the
 *     full data export, or Pro.
 *   - Old Free cloud data (`'legacy_no_cloud'`, or no flag and no reason):
 *     every cloud board and document is first copied to this device, where it
 *     stays editable; then, when flagged, ONE notice with the date.
 *   The cloud copy is never deleted by the client: no timer, no deletion
 *   here — the server owns the schedule.
 *
 * Nothing happens until the SERVER has said the plan has no `cloud_sync` (the
 * signed claim or /auth/usage): a paying account whose plan is still loading
 * must never have its cloud items pulled down. A signed-out session, the web
 * app and recorded demo runs are left alone. A plan WITH `cloud_sync` (an
 * upgrade) gets the cloud scope's parked plan refusals re-sent instead.
 *
 * UI-side only (it reaches `records/itemMove`, which must stay out of the MCP
 * import graph); started by `useCloudItemsReconcile` in App.js.
 */
import i18n from '../i18n';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { setSettingsInitialTab, setSettingsModalOpen } from '../store/uiModalStore';
import { isTauri } from '../utils/isTauri';
import { isSyntheticRun } from '../utils/syntheticRun';
import { copyCloudItemsToDevice, unparkPlanRefusedOps } from '../records/itemMove';
import { bumpDocumentsTree } from '../extensions/bundled/documents/documentsUiStore';
import { bumpBoards } from '../extensions/bundled/task-manager/boardsUiStore';
import { cloudSync } from './cloudAccess';
import { CLOUD_SYNC, capabilitiesFromUsage, formatCloudRemovalDate, readCloudRemoval } from './cloudState';

// Per account, flag and deletion date: a date that arrives later (the
// backend learns it after the app) is news, so it is told once more.
const noticeKey = (userId, flaggedAt, scheduledAt) =>
  `apiboo.cloudRemovalNotice.${userId}.${flaggedAt}${scheduledAt ? `.${scheduledAt}` : ''}`;

function signedInUserId() {
  const { isAuthenticated, user } = useAuthStore.getState();
  return isAuthenticated && user?.id != null ? String(user.id) : null;
}

/** Opens Settings › Privacy & Data, where the GDPR export lives. */
export function openDataExport() {
  setSettingsInitialTab('privacy');
  setSettingsModalOpen(true);
}

/**
 * The removal text: with the deletion date when the server sent one (in the
 * app's language and date format), and saying so when this pass just copied
 * the boards and documents to the device.
 */
export function removalMessage({ scheduledAt, copied = 0 } = {}) {
  const date = formatCloudRemovalDate(scheduledAt, i18n.language);
  if (copied > 0) {
    return date
      ? i18n.t('cloudLocal.removal.copiedDated', { date })
      : i18n.t('cloudLocal.removal.copiedMessage');
  }
  return date ? i18n.t('cloudLocal.removal.messageDated', { date }) : i18n.t('cloudLocal.removal.message');
}

/** The removal notice, once per account, flag and date on this device. */
function noticeRemoval(userId, flaggedAt, scheduledAt, copied) {
  const key = noticeKey(userId, flaggedAt, scheduledAt);
  try {
    if (localStorage.getItem(key) === '1') return false;
    localStorage.setItem(key, '1');
  } catch {
    // Storage unavailable: say it anyway — once per session is still bounded.
  }
  useNotificationStore.getState().addNotification({
    type: 'warning',
    title: i18n.t('cloudLocal.removal.title'),
    message: removalMessage({ scheduledAt, copied }),
    duration: 0, // stays until the user closes it
    action: { label: i18n.t('cloudLocal.removal.export'), onClick: openDataExport },
  });
  return true;
}

function noticeCopied() {
  useNotificationStore.getState().addNotification({
    type: 'info',
    title: i18n.t('cloudLocal.copied.title'),
    message: i18n.t('cloudLocal.copied.message'),
    duration: 0,
  });
}

/**
 * One pass. Resolves what it did: 'none' | 'notice' | 'copied'.
 * `fetchUsage` defaults to the auth store's (it also keeps `user._usage`).
 */
export async function reconcileCloudItems({ fetchUsage } = {}) {
  if (!isTauri() || isSyntheticRun()) return { action: 'none' };
  const userId = signedInUserId();
  if (!userId) return { action: 'none' };

  // Fresh: the removal flag lives only in /auth/usage, which a session fed by
  // the signed claim never asked for.
  let usage = null;
  try {
    usage = await (fetchUsage || useAuthStore.getState().fetchUsage)?.();
  } catch {
    usage = null;
  }
  if (signedInUserId() !== userId) return { action: 'none' }; // signed out / switched meanwhile

  let { allowed, known } = cloudSync();
  if (!known) {
    const caps = capabilitiesFromUsage(usage);
    if (caps) {
      allowed = caps.includes(CLOUD_SYNC);
      known = true;
    }
  }
  if (!known) return { action: 'none' };
  if (allowed) {
    // A plan WITH cloud_sync: what the server refused while the plan had
    // none (parked 402s of the cloud scope) is sent now. Nothing is copied.
    let unparked = 0;
    try {
      ({ unparked = 0 } = (await unparkPlanRefusedOps()) || {});
    } catch (err) {
      return { action: 'failed', error: String(err?.message || err) };
    }
    return unparked > 0 ? { action: 'unparked', unparked } : { action: 'none' };
  }

  // Owner, 16.10.2026: a flagged cloud copy has a real deletion date
  // (`removal_scheduled_at`, 90 days), and the server says WHY it flagged it
  // (`removal_reason`):
  //   - 'downgrade': NO automatic copy — the dated notice with "Download to
  //     this device" / export, as decided for card 3.4;
  //   - 'legacy_no_cloud' (old Free cloud data): copy first, then one notice
  //     with the date;
  //   - no reason: an older backend. Unflagged = old Free data → copy. A flag
  //     without a reason comes from a backend (#217) that flagged ONLY
  //     downgrades, so it is treated as one: never copied on a guess.
  const user = useAuthStore.getState().user;
  const { flaggedAt, scheduledAt, reason } = readCloudRemoval(usage ?? user?._usage, user);

  const isDowngrade = reason === 'downgrade' || (flaggedAt && reason == null);
  if (isDowngrade) {
    if (!flaggedAt) return { action: 'none' };
    noticeRemoval(userId, flaggedAt, scheduledAt, 0);
    return { action: 'notice' };
  }

  let copied = 0;
  try {
    ({ copied = 0 } = (await copyCloudItemsToDevice()) || {});
  } catch (err) {
    // e.g. the engine has not taken the session yet ('signed_out'): the
    // caller tries again later. Nothing was changed, and nothing is said
    // yet: the notice follows the copy.
    return { action: 'failed', error: String(err?.message || err) };
  }
  if (copied > 0) {
    bumpDocumentsTree();
    bumpBoards();
  }

  if (flaggedAt) {
    // One notice: the removal (with the date, and the copy when there was
    // one). Already told about this flag and date: a new copy still says so.
    const told = noticeRemoval(userId, flaggedAt, scheduledAt, copied);
    if (!told && copied > 0) noticeCopied();
    return copied > 0 ? { action: 'notice', copied } : { action: 'notice' };
  }
  if (copied > 0) {
    noticeCopied();
    return { action: 'copied', copied };
  }
  return { action: 'none' };
}
